import {
  ABSTAIN,
  EXTRACT_KINDS,
  formatUsd,
  optionNames,
  questionSchema,
  type Answer,
  type DecideResponse,
  type Question,
} from '@console/shared'
import { useQueryClient } from '@tanstack/react-query'
import { Braces, ChevronDown, Code2, Eraser, List, Play, Plus, Sparkles, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from '../components/ui/Button'
import { Dropdown, Menu } from '../components/ui/Dropdown'
import { Modal } from '../components/ui/Modal'
import { Badge, CopyButton, EmptyState, ErrorText } from '../components/ui/misc'
import { api, useActiveOrg, useConfig, useModels } from '../lib/api'
import { blankQuestion, EXAMPLES } from '../lib/examples'
import { curlSnippet, pythonSnippet, typescriptSnippet } from '../lib/snippets'

type Entry = { key: string; name: string; q: Question }
type RunMeta = { requestId: string; model: string; mock: boolean; costNano: number }

let seq = 0
const keyed = (qs: Record<string, Question>): Entry[] => Object.entries(qs).map(([name, q]) => ({ key: `q${++seq}`, name, q }))
const toObject = (entries: Entry[]) => Object.fromEntries(entries.map((e) => [e.name, e.q]))

/** State is sent as JSON when it parses as JSON, otherwise as plain text (both are valid states). */
function parseState(text: string): unknown {
  const t = text.trim()
  if (!t) return ''
  if (t.startsWith('{') || t.startsWith('[')) {
    try {
      return JSON.parse(t)
    } catch {
      return t
    }
  }
  return t
}

const pct = (x: number) => `${Math.round(x * 100)}%`

// ------------------------------------------------------------------ question editor

function CriteriaEditor({ q, onChange }: { q: Question; onChange: (q: Question) => void }) {
  if (q.type === 'extract') {
    const spec = q.extract ?? { kind: 'any' as const, max_candidates: 48 }
    return (
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-ink-secondary">Kind of value</span>
          <Dropdown
            value={spec.kind}
            options={EXTRACT_KINDS.map((k) => ({ id: k, label: k }))}
            onChange={(k) => onChange({ ...q, criteria: undefined, extract: { ...spec, kind: k } })}
          />
        </div>
        <p className="text-[11px] text-ink-muted">The answer is a span copied from the state, or “not stated”. Nothing is generated.</p>
      </div>
    )
  }
  if (q.type === 'noul') {
    const c = (q.criteria && !Array.isArray(q.criteria) ? q.criteria : {}) as Record<string, string>
    const set = (k: 'yes' | 'no', v: string) => {
      const next = { yes: c.yes ?? '', no: c.no ?? '', [k]: v }
      onChange({ ...q, criteria: next.yes || next.no ? next : undefined })
    }
    return (
      <div className="grid gap-2 sm:grid-cols-2">
        {(['yes', 'no'] as const).map((k) => (
          <label key={k} className="block">
            <span className="mb-0.5 block text-[11px] text-ink-muted">When “{k}” (optional)</span>
            <input className="input" value={c[k] ?? ''} placeholder={k === 'yes' ? 'e.g. every rule was followed' : 'e.g. at least one rule was broken'} onChange={(e) => set(k, e.target.value)} />
          </label>
        ))}
      </div>
    )
  }
  if (q.type === 'choice') {
    const c: Record<string, string> = Array.isArray(q.criteria)
      ? Object.fromEntries(q.criteria.map((n) => [n, '']))
      : ((q.criteria ?? {}) as Record<string, string>)
    const rows = Object.entries(c)
    const write = (next: [string, string][]) => onChange({ ...q, criteria: Object.fromEntries(next) })
    return (
      <div className="space-y-1.5">
        {rows.map(([name, desc], i) => (
          <div key={i} className="flex gap-1.5">
            <input
              className="input !w-40 shrink-0 font-mono !text-[12px]"
              value={name}
              placeholder="option_name"
              onChange={(e) => write(rows.map((r, j) => (j === i ? [e.target.value, r[1]] : r)))}
            />
            <input className="input" value={desc} placeholder="What this option means (helps the model)" onChange={(e) => write(rows.map((r, j) => (j === i ? [r[0], e.target.value] : r)))} />
            <button type="button" className="btn-ghost !px-1.5" aria-label="Remove option" onClick={() => write(rows.filter((_, j) => j !== i))}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <button type="button" className="btn-ghost !h-7 !px-2 !text-[12px]" onClick={() => write([...rows, [`option_${rows.length + 1}`, '']])}>
          <Plus className="h-3.5 w-3.5" /> Add option
        </button>
        <p className="text-[11px] text-ink-muted">“{ABSTAIN}” (can't tell) is added automatically.</p>
      </div>
    )
  }
  const levels = Array.isArray(q.criteria) ? q.criteria : Object.keys(q.criteria ?? {})
  const write = (next: string[]) => onChange({ ...q, criteria: next })
  return (
    <div className="space-y-1.5">
      {levels.map((l, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <span className="w-5 shrink-0 text-right text-[11px] tabular-nums text-ink-disabled">{i}</span>
          <input className="input" value={l} placeholder={`Level ${i}`} onChange={(e) => write(levels.map((x, j) => (j === i ? e.target.value : x)))} />
          <button type="button" className="btn-ghost !px-1.5" aria-label="Remove level" disabled={levels.length <= 2} onClick={() => write(levels.filter((_, j) => j !== i))}>
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      <button type="button" className="btn-ghost !h-7 !px-2 !text-[12px]" disabled={levels.length >= 10} onClick={() => write([...levels, ''])}>
        <Plus className="h-3.5 w-3.5" /> Add level
      </button>
      <p className="text-[11px] text-ink-muted">Lowest first. 2–10 levels.</p>
    </div>
  )
}

function QuestionCard({ entry, onChange, onRemove, error }: { entry: Entry; onChange: (e: Entry) => void; onRemove: () => void; error?: string }) {
  const [open, setOpen] = useState(true)
  const { q } = entry
  return (
    <div className={`rounded-md border ${error ? 'border-[color:var(--feedback-error)]' : 'border-line'} bg-surface`}>
      <div className="flex items-center gap-2 px-3 py-2">
        <button type="button" onClick={() => setOpen((v) => !v)} aria-label="Toggle" className="text-ink-muted">
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? '' : '-rotate-90'}`} />
        </button>
        <input
          className="min-w-0 flex-1 bg-transparent font-mono text-[12.5px] font-medium text-ink outline-none"
          value={entry.name}
          onChange={(e) => onChange({ ...entry, name: e.target.value.replace(/\s+/g, '_') })}
          aria-label="Question name"
        />
        <Dropdown
          value={q.type}
          align="right"
          options={[
            { id: 'noul', label: 'Noul', hint: 'yes / no' },
            { id: 'choice', label: 'Choice', hint: 'pick one of N' },
            { id: 'score', label: 'Score', hint: 'ordered levels' },
            { id: 'extract', label: 'Extract', hint: 'a value from the state' },
          ]}
          onChange={(t) => onChange({ ...entry, q: { ...blankQuestion(t), instructions: q.instructions } })}
        />
        <button type="button" className="btn-ghost !px-1.5" onClick={onRemove} aria-label="Remove question">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      {open && (
        <div className="space-y-2.5 border-t border-line-subtle px-3 py-3">
          <textarea
            className="input min-h-[52px] !text-[12.5px]"
            value={typeof q.instructions === 'string' ? q.instructions : JSON.stringify(q.instructions)}
            onChange={(e) => onChange({ ...entry, q: { ...q, instructions: e.target.value } })}
            placeholder="The question, in plain words"
          />
          <CriteriaEditor q={q} onChange={(nq) => onChange({ ...entry, q: nq })} />
          {error && <p className="text-[11.5px] text-danger">{error}</p>}
        </div>
      )}
    </div>
  )
}

// ------------------------------------------------------------------ results

function Bar({ value, tone = 'brand' }: { value: number; tone?: 'brand' | 'muted' }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-sunken">
      <div className={`h-full rounded-full ${tone === 'brand' ? 'bg-brand' : 'bg-[color:var(--border-strong)]'}`} style={{ width: `${Math.max(1, value * 100)}%` }} />
    </div>
  )
}

function AnswerView({ name, a, q }: { name: string; a: Answer; q?: Question }) {
  let headline = ''
  if (a.type === 'noul') headline = `${pct(a.noul)} yes`
  else if (a.type === 'choice') headline = `${a.choice} · ${pct(a.probabilities[a.choice] ?? 0)}`
  else if (a.type === 'extract') headline = a.present ? `“${a.value}” · ${pct(a.candidates[0]?.probability ?? 0)}` : 'not stated'
  else {
    const levels = q ? optionNames(q) : []
    const top = Object.entries(a.probabilities).sort((x, y) => y[1] - x[1])[0]
    headline = `${a.score.toFixed(2)} expected${top ? ` · most likely “${levels[Number(top[0])] ?? top[0]}”` : ''}`
  }
  return (
    <div className="border-b border-line-subtle py-3 last:border-b-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-[12.5px] font-semibold text-ink">{name}</div>
          {q && typeof q.instructions === 'string' && <div className="text-[12px] text-ink-muted">{q.instructions}</div>}
        </div>
        <div className="shrink-0 text-right text-[12.5px] font-medium tabular-nums text-ink">{headline}</div>
      </div>
      <div className="mt-2 space-y-1.5">
        {a.type === 'noul' && (
          <div className="grid grid-cols-[52px_1fr_44px] items-center gap-2 text-[12px]">
            <span className="text-ink-secondary">yes</span>
            <Bar value={a.noul} />
            <span className="text-right tabular-nums">{pct(a.noul)}</span>
          </div>
        )}
        {a.type === 'extract' &&
          a.candidates.map((c) => (
            <div key={`${c.span[0]}-${c.span[1]}`} className="grid grid-cols-[minmax(80px,180px)_1fr_44px] items-center gap-2 text-[12px]">
              <span className={`truncate ${a.present && c.value === a.value ? 'font-semibold text-ink' : 'text-ink-secondary'}`} title={`${c.value} [${c.span[0]}, ${c.span[1]})`}>
                {c.value}
              </span>
              <Bar value={c.probability} tone={a.present && c.value === a.value ? 'brand' : 'muted'} />
              <span className="text-right tabular-nums">{pct(c.probability)}</span>
            </div>
          ))}
        {a.type !== 'noul' && a.type !== 'extract' &&
          Object.entries(a.probabilities)
            .sort((x, y) => (a.type === 'score' ? Number(x[0]) - Number(y[0]) : y[1] - x[1]))
            .map(([opt, p]) => {
              const label = a.type === 'score' ? (a.legend?.[opt] ?? (q ? optionNames(q)[Number(opt)] : opt)) : opt
              const top = a.type === 'choice' ? opt === a.choice : false
              return (
                <div key={opt} className="grid grid-cols-[minmax(80px,180px)_1fr_44px] items-center gap-2 text-[12px]">
                  <span className={`truncate ${top ? 'font-semibold text-ink' : 'text-ink-secondary'}`} title={label}>
                    {a.type === 'score' ? `${opt} · ${label}` : label}
                  </span>
                  <Bar value={p} tone={top || a.type === 'score' ? 'brand' : 'muted'} />
                  <span className="text-right tabular-nums">{pct(p)}</span>
                </div>
              )
            })}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {'confidence' in a && a.confidence != null && <Badge>confidence {pct(a.confidence)}</Badge>}
        {a.abstain != null && <Badge tone={a.abstain > 0.3 ? 'warning' : 'neutral'}>can't tell {pct(a.abstain)}</Badge>}
      </div>
    </div>
  )
}

// ------------------------------------------------------------------ page

export function PlaygroundPage() {
  const { can } = useActiveOrg()
  if (!can('playground.run'))
    return (
      <EmptyState icon={<Play className="h-8 w-8" />} title="The playground spends the organization's credits">
        Your role can't run requests. Ask an admin of the organization for the developer role.
      </EmptyState>
    )
  return <Playground />
}

function Playground() {
  const { data: models } = useModels()
  const qc = useQueryClient()
  const [model, setModel] = useState<string>('')
  const [stateText, setStateText] = useState('')
  const [entries, setEntries] = useState<Entry[]>([])
  const [mode, setMode] = useState<'form' | 'json'>('form')
  const [jsonText, setJsonText] = useState('{}')
  const [jsonErr, setJsonErr] = useState<string | null>(null)
  const [abstain, setAbstain] = useState(true)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [result, setResult] = useState<{ response: DecideResponse; meta: RunMeta; ms: number; questions: Record<string, Question> } | null>(null)
  const [view, setView] = useState<'answers' | 'json'>('answers')
  const [codeOpen, setCodeOpen] = useState(false)

  const modelList = models?.data ?? []
  useEffect(() => {
    if (!model && modelList.length) setModel((modelList.find((m) => m.isDefault) ?? modelList[0]!).id)
  }, [model, modelList])

  const questions = useMemo(() => toObject(entries), [entries])
  const problems = useMemo(() => {
    const out: Record<string, string> = {}
    const seen = new Set<string>()
    for (const e of entries) {
      if (!e.name) out[e.key] = 'Give the question a name'
      else if (seen.has(e.name)) out[e.key] = 'Question names must be unique'
      seen.add(e.name)
      const r = questionSchema.safeParse(e.q)
      if (!r.success && !out[e.key]) out[e.key] = r.error.issues[0]?.message ?? 'Invalid question'
    }
    return out
  }, [entries])

  const switchMode = (m: 'form' | 'json') => {
    if (m === 'json') {
      setJsonText(JSON.stringify(questions, null, 2))
      setJsonErr(null)
    } else {
      try {
        const parsed = JSON.parse(jsonText) as Record<string, Question>
        setEntries(keyed(parsed))
        setJsonErr(null)
      } catch (e) {
        setJsonErr((e as Error).message)
        return
      }
    }
    setMode(m)
  }

  const currentQuestions = (): Record<string, Question> | null => {
    if (mode === 'form') return questions
    try {
      return JSON.parse(jsonText) as Record<string, Question>
    } catch (e) {
      setJsonErr((e as Error).message)
      return null
    }
  }

  const body = useMemo(() => {
    const qs = mode === 'form' ? questions : (() => { try { return JSON.parse(jsonText) } catch { return {} } })()
    return { model, state: parseState(stateText), questions: qs, ...(abstain ? {} : { options: { abstain: false } }) }
  }, [model, stateText, questions, mode, jsonText, abstain])

  const canRun = Boolean(stateText.trim()) && (mode === 'json' || (entries.length > 0 && Object.keys(problems).length === 0))

  const run = useCallback(async () => {
    const qs = currentQuestions()
    if (!qs || !canRun) return
    setRunning(true)
    setError(null)
    const t0 = performance.now()
    try {
      const r = await api<{ response: DecideResponse; meta: RunMeta }>('/api/playground/decide', { body: { ...body, questions: qs } })
      setResult({ ...r, ms: Math.round(performance.now() - t0), questions: qs })
      setView('answers')
      if (r.meta.costNano > 0) void qc.invalidateQueries({ queryKey: ['billing'] })
    } catch (e) {
      setError(e)
    } finally {
      setRunning(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [body, canRun])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault()
        void run()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [run])

  const load = (id: string) => {
    const ex = EXAMPLES.find((x) => x.id === id)
    if (!ex) return
    setStateText(typeof ex.state === 'string' ? ex.state : JSON.stringify(ex.state, null, 2))
    setEntries(keyed(ex.questions))
    setJsonText(JSON.stringify(ex.questions, null, 2))
    setResult(null)
    setError(null)
  }

  const add = (type: Question['type']) => {
    const names = new Set(entries.map((e) => e.name))
    let n = 1
    while (names.has(`${type}_${n}`)) n++
    setEntries([...entries, { key: `q${++seq}`, name: `${type}_${n}`, q: blankQuestion(type) }])
  }

  const empty = !stateText.trim() && entries.length === 0

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-11 shrink-0 items-center gap-2 border-b border-line px-3 md:px-4">
        <h1 className="text-[13px] font-medium text-ink">Playground</h1>
        <Menu label="Examples" items={EXAMPLES.map((e) => ({ id: e.id, label: e.title, hint: e.subtitle, onSelect: () => load(e.id) }))} />
        <Button
          variant="ghost"
          className="!h-7 !text-[12px]"
          onClick={() => {
            setStateText('')
            setEntries([])
            setJsonText('{}')
            setResult(null)
            setError(null)
          }}
        >
          <Eraser className="h-3.5 w-3.5" /> Clear
        </Button>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" className="!h-7 !text-[12px]" onClick={() => setCodeOpen(true)} disabled={!canRun}>
            <Code2 className="h-3.5 w-3.5" /> Get code
          </Button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        {/* request */}
        <div className="flex min-h-0 min-w-0 flex-col border-b border-line lg:w-[56%] lg:border-b-0 lg:border-r">
          <div className="min-h-0 flex-1 overflow-y-auto">
            <section className="border-b border-line">
              <div className="flex h-8 items-center gap-2 border-b border-line-subtle px-3">
                <span className="text-[12px] font-medium text-ink">State</span>
                <span className="text-[11px] text-ink-muted">JSON or plain text: a trace, conversation, document, tool catalogue…</span>
              </div>
              <textarea
                className="block h-56 w-full resize-y bg-transparent px-3 py-2 font-mono text-[12.5px] leading-relaxed text-ink outline-none placeholder:text-ink-disabled"
                value={stateText}
                onChange={(e) => setStateText(e.target.value)}
                spellCheck={false}
                placeholder={'{\n  "messages": [ … ]\n}'}
                aria-label="State"
              />
            </section>
            <section>
              <div className="flex h-8 items-center gap-2 border-b border-line-subtle px-3">
                <span className="text-[12px] font-medium text-ink">Questions</span>
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full border border-line px-1 text-[10px] text-ink-muted">
                  {mode === 'form' ? entries.length : '·'}
                </span>
                <div className="ml-auto flex items-center gap-0.5 rounded-md border border-line p-0.5">
                  <button type="button" title="Form" className={`inline-flex h-6 w-6 items-center justify-center rounded ${mode === 'form' ? 'bg-surface-hover text-ink' : 'text-ink-muted'}`} onClick={() => switchMode('form')}>
                    <List className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" title="JSON" className={`inline-flex h-6 w-6 items-center justify-center rounded ${mode === 'json' ? 'bg-surface-hover text-ink' : 'text-ink-muted'}`} onClick={() => switchMode('json')}>
                    <Braces className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              {mode === 'form' ? (
                <div className="space-y-2 p-3">
                  {entries.map((e, i) => (
                    <QuestionCard
                      key={e.key}
                      entry={e}
                      error={problems[e.key]}
                      onChange={(ne) => setEntries(entries.map((x, j) => (j === i ? ne : x)))}
                      onRemove={() => setEntries(entries.filter((_, j) => j !== i))}
                    />
                  ))}
                  <div className="flex flex-wrap gap-1.5">
                    {(['noul', 'choice', 'score', 'extract'] as const).map((t) => (
                      <Button key={t} variant="secondary" className="!h-7 !text-[12px]" onClick={() => add(t)}>
                        <Plus className="h-3.5 w-3.5" />
                        {t === 'noul' ? 'Yes / no' : t === 'choice' ? 'Choice' : t === 'score' ? 'Score' : 'Extract'}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="p-3">
                  <textarea
                    className="input min-h-[280px] font-mono !text-[12px]"
                    value={jsonText}
                    spellCheck={false}
                    onChange={(e) => {
                      setJsonText(e.target.value)
                      setJsonErr(null)
                    }}
                  />
                  {jsonErr && <p className="mt-1 text-[11.5px] text-danger">{jsonErr}</p>}
                </div>
              )}
            </section>
          </div>
          <div className="flex h-12 shrink-0 items-center gap-2 border-t border-line bg-surface-sunken px-3">
            <Dropdown
              value={model}
              up
              options={modelList.map((m) => ({ id: m.id, label: m.id, hint: `$${m.usdPerMillionInputTokens}/1M input tokens` }))}
              onChange={setModel}
            />
            <label className="flex items-center gap-1.5 text-[12px] text-ink-secondary">
              <input type="checkbox" checked={abstain} onChange={(e) => setAbstain(e.target.checked)} className="accent-[color:var(--trodo-primary)]" />
              Allow “can't tell”
            </label>
            <div className="ml-auto flex items-center gap-2">
              <span className="hidden text-[11px] text-ink-disabled sm:inline">⌘/Ctrl + Enter</span>
              <Button onClick={run} loading={running} disabled={!canRun} className="h-8">
                {!running && <Play className="h-3.5 w-3.5" />} Run
              </Button>
            </div>
          </div>
        </div>

        {/* response */}
        <aside className="flex min-h-[320px] min-w-0 flex-1 flex-col">
          <div className="flex h-11 shrink-0 items-center gap-2 border-b border-line px-4">
            <span className="text-[13px] font-medium text-ink">Response</span>
            {result && (
              <span className="text-[11px] text-ink-muted">
                {result.ms} ms · {result.response.usage.input_tokens.toLocaleString()} tokens · {formatUsd(result.meta.costNano)}
              </span>
            )}
            {result && (
              <div className="ml-auto flex items-center gap-0.5 rounded-md border border-line p-0.5">
                <button type="button" title="Answers" className={`inline-flex h-6 w-6 items-center justify-center rounded ${view === 'answers' ? 'bg-surface-hover text-ink' : 'text-ink-muted'}`} onClick={() => setView('answers')}>
                  <List className="h-3.5 w-3.5" />
                </button>
                <button type="button" title="JSON" className={`inline-flex h-6 w-6 items-center justify-center rounded ${view === 'json' ? 'bg-surface-hover text-ink' : 'text-ink-muted'}`} onClick={() => setView('json')}>
                  <Braces className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
            <ErrorText error={error} />
            {result ? (
              view === 'answers' ? (
                <div>
                  {Object.entries(result.response.answers).map(([name, a]) => (
                    <AnswerView key={name} name={name} a={a} q={result.questions[name]} />
                  ))}
                  <p className="mt-3 text-[11px] text-ink-disabled">request {result.meta.requestId}</p>
                </div>
              ) : (
                <div>
                  <div className="mb-2 flex justify-end">
                    <CopyButton value={JSON.stringify(result.response, null, 2)} />
                  </div>
                  <pre className="overflow-auto rounded-md border border-line bg-surface-sunken p-3 text-[12px] leading-relaxed text-ink">{JSON.stringify(result.response, null, 2)}</pre>
                </div>
              )
            ) : empty ? (
              <div className="space-y-2">
                <div className="mb-3 flex items-center gap-2 text-[12px] font-medium text-ink-muted">
                  <Sparkles className="h-3.5 w-3.5" /> Start from an example
                </div>
                {EXAMPLES.map((ex) => (
                  <button key={ex.id} type="button" onClick={() => load(ex.id)} className="block w-full rounded-md border border-line px-3 py-2.5 text-left hover:bg-surface-hover">
                    <div className="text-[13px] font-medium text-ink">{ex.title}</div>
                    <div className="text-[12px] text-ink-muted">{ex.subtitle}</div>
                  </button>
                ))}
              </div>
            ) : (
              !error && <p className="text-[13px] text-ink-muted">Run the request to see each answer's probabilities.</p>
            )}
          </div>
        </aside>
      </div>

      <CodeModal open={codeOpen} onClose={() => setCodeOpen(false)} body={body} />
    </div>
  )
}

function CodeModal({ open, onClose, body }: { open: boolean; onClose: () => void; body: unknown }) {
  const [lang, setLang] = useState<'curl' | 'python' | 'typescript'>('curl')
  const base = useConfig().data?.publicApiUrl ?? window.location.origin
  const code = lang === 'curl' ? curlSnippet(body, base) : lang === 'python' ? pythonSnippet(body, base) : typescriptSnippet(body, base)
  return (
    <Modal open={open} onClose={onClose} title="Call this from your code" wide>
      <div className="mb-2 flex items-center gap-1">
        {(['curl', 'python', 'typescript'] as const).map((l) => (
          <button key={l} type="button" onClick={() => setLang(l)} className={`rounded-md px-2.5 py-1 text-[12px] ${lang === l ? 'bg-surface-hover font-medium text-ink' : 'text-ink-muted'}`}>
            {l === 'curl' ? 'cURL' : l === 'python' ? 'Python' : 'TypeScript'}
          </button>
        ))}
        <div className="ml-auto">
          <CopyButton value={code} />
        </div>
      </div>
      <pre className="max-h-[50vh] overflow-auto rounded-md border border-line bg-surface-sunken p-3 text-[11.5px] leading-relaxed text-ink">{code}</pre>
      <p className="mt-2 text-[12px]">Replace <code>$TRODO_API_KEY</code> with a key from API Keys.</p>
    </Modal>
  )
}
